import { TFunction } from "i18next";
import { setToastMessage } from "../store/slices/app/appSlice";
import { Dispatch } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

/**
 * Interface for platform-specific metrics data
 */
interface PlatformMetrics {
  conversions: number;
  dailyCTR: number;
  completeViews: number;
  dailyImpressions: number;
  dailyInvestment: number;
  vtr: number;
  cpv: number;
  cost: number;
  cpc: number;
  cpm: number;
  ctr: number;
  clicks: number;
  impressions: number;
}

/**
 * Interface for platform data
 */
interface PlatformData {
  name: string;
  platform: string;
  metrics: PlatformMetrics;
}

/**
 * Interface for campaign metrics data
 */
interface CampaignMetric {
  date: string;
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  cpc: number;
  cost: number;
  cpv: number;
  vtr: number;
  dailyInvestment: number;
  dailyImpressions: number;
  completeViews: number;
  dailyCTR: number;
  conversions: number;
  platforms?: Array<{
    name: string;
    platform: string;
    metrics: {
      conversions: number;
      dailyCTR: number;
      completeViews: number;
      dailyImpressions: number;
      dailyInvestment: number;
      vtr: number;
      cpv: number;
      cost: number;
      cpc: number;
      cpm: number;
      ctr: number;
      clicks: number;
      impressions: number;
    };
  }>;
  // Using a more specific type for the index signature to accommodate complex objects like platforms
  [key: string]:
    | string
    | number
    | undefined
    | Array<PlatformData>
    | Record<string, unknown>;
}

/**
 * Export campaign metrics data to CSV
 * @param metrics - Array of campaign metrics
 * @param campaignName - Name of the campaign
 * @param t - Translation function
 * @param dispatch - Redux dispatch function
 * @returns void
 */
export const exportMetricsToCSV = (
  metrics: CampaignMetric[] | undefined,
  campaignName: string,
  t: TFunction,
  dispatch: Dispatch
): void => {
  if (!metrics || metrics.length === 0) {
    // Show error toast if no data to export
    dispatch(
      setToastMessage({
        message: t("campaigns.noDataToExport", {
          defaultValue: "No data available to export",
        }),
        severity: "warning",
      })
    );
    return;
  }

  try {
    // Define all possible metrics columns and their corresponding property names
    const metricsColumns = [
      { header: "Date", property: "date" },
      { header: "Cost", property: "cost" },
      { header: "CTR", property: "ctr" },
      { header: "CPC", property: "cpc" },
      { header: "CPM", property: "cpm" },
      { header: "VTR", property: "vtr" },
      { header: "CPV", property: "cpv" },
      { header: "Impressions", property: "impressions" },
      { header: "Clicks", property: "clicks" },
      { header: "Daily Investment", property: "dailyInvestment" },
      { header: "Daily Impressions", property: "dailyImpressions" },
      { header: "Complete Views", property: "completeViews" },
      { header: "Daily CTR", property: "dailyCTR" },
      { header: "Conversions", property: "conversions" },
      { header: "URL", property: "url" },
    ];

    // Filter out columns where all values are 0 (except for the date column)
    const filteredColumns = metricsColumns.filter((column) => {
      // Always include the date column
      if (column.property === "date") return true;

      // Check if all values for this property are 0
      const allZeros = metrics.every((metric) => {
        const value = metric[column.property];
        // Check for 0, "0", undefined, null, or very small numbers (floating point precision issues)
        return (
          value === 0 ||
          value === "0" ||
          value === undefined ||
          value === null ||
          (typeof value === "number" && Math.abs(value) < 0.0000001)
        );
      });

      return !allZeros;
    });

    // Create CSV header row from filtered columns
    const headers = filteredColumns.map((column) => column.header);

    // Create CSV rows from metrics data, only including filtered columns
    const rows = metrics.map((metric) =>
      filteredColumns.map((column) => metric[column.property])
    );

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create a Blob with the CSV data, adding UTF-8 BOM for Excel compatibility
    // The BOM (Byte Order Mark) helps Excel detect UTF-8 encoding correctly
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([BOM, csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    // Create a download link and trigger the download
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `campaign_metrics_${campaignName || "export"}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Show success toast
    dispatch(
      setToastMessage({
        message: t("campaigns.exportSuccess", {
          defaultValue: "Metrics data exported successfully",
        }),
        severity: "success",
      })
    );
  } catch (error) {
    Sentry.captureException(error);
    console.error("Error exporting metrics data:", error);

    // Show error toast
    dispatch(
      setToastMessage({
        message: t("campaigns.exportError", {
          defaultValue: "Error exporting metrics data",
        }),
        severity: "error",
      })
    );
  }
};
